/**
 * File-validation primitives. Pure, side-effect-free, UI-agnostic.
 *
 * SRP: this module ONLY validates files (extension + size). It does not
 * render, parse, upload, or read file contents. Compose it from a hook or
 * call it inline.
 *
 * Usage:
 *   import { isAllowedFile, formatFileSize, extensionsToAccept } from "utils/file-upload"
 *
 *   if (!isAllowedFile(file, ["csv", "xlsx"])) toast.error("Unsupported type")
 *   <input accept={extensionsToAccept(["csv", "xlsx"])} />
 */

export const DEFAULT_ALLOWED_EXTENSIONS = ["csv", "xls", "xlsx"]

/** Lowercased extension without the leading dot, or "" if none. */
export const getFileExtension = (file: File): string =>
  file.name.split(".").pop()?.toLowerCase() ?? ""

/** Same as getFileExtension, but for a raw filename string (e.g. from an API response). */
export const getExtensionFromFileName = (fileName: string): string =>
  fileName.split(".").pop()?.toLowerCase() ?? ""

/** True if the file's extension is in the allow-list (case-insensitive). */
export const isAllowedFile = (
  file: File,
  allowedExtensions: string[] = DEFAULT_ALLOWED_EXTENSIONS
): boolean => allowedExtensions.includes(getFileExtension(file))

/** Bytes → "X.YZ" MB string. No unit suffix; caller appends "MB". */
export const formatFileSize = (bytes: number): string =>
  (bytes / (1024 * 1024)).toFixed(2)

/** True when size <= max (MB). Use for the size guard before parsing. */
export const isWithinSizeLimit = (file: File, maxSizeMB: number): boolean =>
  file.size <= maxSizeMB * 1024 * 1024

/**
 * Convert a list of extensions to an HTML <input accept="..."> value.
 *   ["csv", "xlsx"] -> ".csv,.xlsx"
 */
export const extensionsToAccept = (extensions: string[]): string =>
  extensions.map((ext) => `.${ext}`).join(",")
